import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  TextField,
  Pagination,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Menu,
  Grid,
  Collapse
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Science as ScienceIcon,
  Architecture as ArchitectureIcon,
  BiotechOutlined as BiotechIcon,
  Folder as FolderIcon,
  MoreVert as MoreIcon,
  Tune as TuneIcon,
  ExpandMore,
  ExpandLess
} from '@mui/icons-material';
import { ModelType } from '@/types/colabfold';
import api from '@/services/api';
import { deleteJob } from '@/api/jobs';
import { useAwsSettings } from '@/hooks/useAwsSettings';
import FolderTree from '@/components/FolderTree';
import { addJobsToFolder, removeJobsFromFolder, createFolder } from '@/api/folders';
import { quotaService } from '@/services/api';

interface Job {
  id: string;
  jobname: string;
  status: string;
  model_type: string;
  created_at: string;
  updated_at: string;
  folder: string | null;
  is_batch?: boolean;
  child_jobs_count?: number;
  child_jobs?: Job[];
}

interface AllJobsListProps {
  // No props needed
}

const AllJobsList: React.FC<AllJobsListProps> = () => {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [deleteJobId, setDeleteJobId] = useState<string | null>(null);
  const [deleteJobModelType, setDeleteJobModelType] = useState<string>('');
  const [filterModelType, setFilterModelType] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [modelSelectionOpen, setModelSelectionOpen] = useState(false);
  
  // Folder state
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [jobActionsAnchorEl, setJobActionsAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedJobForAction, setSelectedJobForAction] = useState<Job | null>(null);
  const [folderDialogOpen, setFolderDialogOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(true);
  
  const [newFolderDialogOpen, setNewFolderDialogOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderDescription, setNewFolderDescription] = useState('');
  
  const [folderRefreshTrigger, setFolderRefreshTrigger] = useState(0);
  
  // Batch job expanded state
  const [expandedBatchJobs, setExpandedBatchJobs] = useState<{[key: string]: boolean}>({});
  const [loadingChildJobs, setLoadingChildJobs] = useState<{[key: string]: boolean}>({});
  const [childJobs, setChildJobs] = useState<{[key: string]: Job[]}>({});
  
  const [userQuota, setUserQuota] = useState<number | null>(null);
  const [quotaError, setQuotaError] = useState<string | null>(null);
  
  const navigate = useNavigate();
  const location = useLocation();
  const { isComplete: hasCompleteAwsSettings, loading: awsSettingsLoading } = useAwsSettings();

  // Extract folder ID from location state if available
  useEffect(() => {
    if (location.state && 'selectedFolderId' in location.state) {
      setSelectedFolderId(location.state.selectedFolderId);
    }
  }, [location]);

  useEffect(() => {
    const fetchUserQuota = async () => {
      try {
        const quotaData = await quotaService.getUserQuota();
        console.log('Quota data:', quotaData);  // Add logging
        if (quotaData && typeof quotaData.job_quota === 'number') {
          setUserQuota(quotaData.job_quota);
          setQuotaError(null);
        } else {
          console.error('Invalid quota data received:', quotaData);
          setUserQuota(null);
          setQuotaError('Failed to fetch user quota');
        }
      } catch (error) {
        console.error('Error fetching user quota:', error);
        setUserQuota(null);
        setQuotaError('Failed to fetch user quota');
      }
    };
    fetchUserQuota();
  }, []);

  const fetchJobs = useCallback(async () => {
    try {
      setLoading(true);
      let url = `/api/jobs/?page=${page}`;
      
      if (searchTerm) {
        url += `&search=${searchTerm}`;
      }
      
      if (filterModelType) {
        url += `&model_type=${filterModelType}`;
      }
      
      if (filterStatus) {
        url += `&status=${filterStatus}`;
      }
      
      // Add folder filter if a folder is selected
      if (selectedFolderId) {
        url += `&folder=${selectedFolderId}`;
      }
      
      // Add a timestamp to bypass caching
      url += `&_t=${new Date().getTime()}`;
      
      const response = await api.get(url);
      setJobs(response.data?.results || []);
      setTotalPages(Math.ceil(response.data?.count / 10) || 1);
      if (error) setError(null);
    } catch (error) {
      console.error('Error fetching jobs:', error);
      setJobs([]);
      setTotalPages(1);
      setError('Failed to fetch jobs');
    } finally {
      setLoading(false);
    }
  }, [page, searchTerm, filterModelType, filterStatus, selectedFolderId, error]);

  useEffect(() => {
    fetchJobs();
    const interval = setInterval(fetchJobs, 10000); // Poll every 10 seconds
    
    return () => clearInterval(interval);
  }, [fetchJobs]);

  // Function to fetch child jobs when expanding a batch job
  const fetchChildJobs = async (jobId: string) => {
    try {
      setLoadingChildJobs(prev => ({ ...prev, [jobId]: true }));
      // Get the parent job's model type
      const parentJob = jobs.find(job => job.id === jobId);
      if (!parentJob) {
        throw new Error('Parent job not found');
      }
      const response = await api.get(`/api/${parentJob.model_type}/jobs/${jobId}/?include_child_jobs=true`);
      if (response.data.child_jobs) {
        setChildJobs(prev => ({ ...prev, [jobId]: response.data.child_jobs }));
      }
    } catch (error) {
      console.error('Error fetching child jobs:', error);
    } finally {
      setLoadingChildJobs(prev => ({ ...prev, [jobId]: false }));
    }
  };

  const toggleBatchExpand = (jobId: string) => {
    const newExpandedState = !expandedBatchJobs[jobId];
    setExpandedBatchJobs(prev => ({
      ...prev,
      [jobId]: newExpandedState
    }));
    
    // If expanding and we don't have the child jobs yet, fetch them
    if (newExpandedState && (!childJobs[jobId] || childJobs[jobId].length === 0)) {
      fetchChildJobs(jobId);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'running':
        return 'info';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  const getModelTypeLabel = (modelType: string) => {
    switch (modelType) {
      case 'colabfold':
        return 'ColabFold';
      case 'rfdiffusion':
        return 'RF Diffusion';
      case 'esmif':
        return 'ESM-IF';
      default:
        return modelType;
    }
  };

  const handleDelete = async () => {
    if (!deleteJobId || !deleteJobModelType) return;
    
    try {
      setError(null);
      await deleteJob(deleteJobModelType as ModelType, deleteJobId);
      
      // Increment the trigger to refresh the folder tree
      setFolderRefreshTrigger(prev => prev + 1);
      
      fetchJobs();
    } catch (error) {
      console.error('Error deleting job:', error);
      setError(error instanceof Error ? error.message : 'Failed to delete job. Please try again.');
    } finally {
      setDeleteJobId(null);
      setDeleteJobModelType('');
    }
  };

  const handleViewJob = (job: Job) => {
    if (job.is_batch) {
      toggleBatchExpand(job.id);
    } else {
      navigate(`/${job.model_type}/jobs/${job.id}`);
    }
  };

  const handleViewChildJob = (childJob: Job, e: React.MouseEvent) => {
    e.stopPropagation();  // Prevent toggling the parent batch
    navigate(`/${childJob.model_type}/jobs/${childJob.id}`);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setPage(1); // Reset to first page on new search
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const handleNewJobClick = () => {
    if (userQuota !== null && userQuota <= 0) {
      setQuotaError('You have no job quota left. Please contact admin to increase your quota.');
      return;
    }
    setModelSelectionOpen(true);
  };

  const handleCloseQuotaError = () => {
    setQuotaError(null);
  };

  const handleModelSelect = (modelType: ModelType) => {
    setModelSelectionOpen(false);
    // If a folder is selected, pass it to the new job page
    if (selectedFolderId) {
      navigate(`/${modelType}/new`, { 
        state: { 
          folderId: selectedFolderId,
          folderName: location.state?.folderName || 'Unnamed Folder'
        } 
      });
    } else {
      navigate(`/${modelType}/new`);
    }
  };

  // Folder actions
  const handleFolderSelect = (folderId: string | null) => {
    setSelectedFolderId(folderId);
    setPage(1); // Reset to first page when changing folders
  };

  const handleJobActionsClose = () => {
    setJobActionsAnchorEl(null);
    setSelectedJobForAction(null);
  };

  const handleJobActionsOpen = (event: React.MouseEvent<HTMLElement>, job: Job) => {
    event.stopPropagation();
    setJobActionsAnchorEl(event.currentTarget);
    setSelectedJobForAction(job);
  };

  const handleAddToFolder = () => {
    setJobActionsAnchorEl(null);
    setFolderDialogOpen(true);
  };

  const handleAddJobToFolder = async (folderId: string | null) => {
    if (!folderId || !selectedJobForAction) return;
    
    try {
      await addJobsToFolder(folderId, [selectedJobForAction.id]);
      
      // Increment the trigger to refresh the folder tree
      setFolderRefreshTrigger(prev => prev + 1);
      
      // If we're viewing a specific folder, maintain that view
      if (selectedFolderId) {
        // Force a complete refetch to ensure latest data
        await fetchJobs();
        // If we just moved to the current folder, the job may not appear
        // due to caching issues, so refresh again after a small delay
        setTimeout(() => {
          fetchJobs();
        }, 300);
      } else {
        // If we're in "All Jobs" view, we could either:
        // 1. Stay in All Jobs view (by just calling fetchJobs())
        // 2. Navigate to the folder we just added the job to
        
        // Option 2: Navigate to the folder we just added to
        // Find folder name
        const response = await fetch(`/api/folders/${folderId}/`);
        const folderData = await response.json();
        
        setSelectedFolderId(folderId);
        // Navigate with replace to prevent history build-up
        navigate('/jobs', { 
          state: { 
            selectedFolderId: folderId,
            folderName: folderData.name
          },
          replace: true 
        });
        
        // Ensure data is fetched after navigation
        setTimeout(() => {
          fetchJobs();
        }, 300);
      }
      
      setFolderDialogOpen(false);
    } catch (error) {
      console.error('Error adding job to folder:', error);
      setError('Failed to add job to folder. Please try again.');
    }
  };

  const handleRemoveFromFolder = async () => {
    if (!selectedJobForAction || !selectedJobForAction.folder) return;
    
    try {
      await removeJobsFromFolder(selectedJobForAction.folder, [selectedJobForAction.id]);
      
      // Increment the trigger to refresh the folder tree
      setFolderRefreshTrigger(prev => prev + 1);
      
      // Force a complete refetch to ensure latest data
      await fetchJobs();
      
      // Refresh again after a small delay to ensure changes are reflected
      setTimeout(() => {
        fetchJobs();
      }, 300);
      
      handleJobActionsClose();
    } catch (error) {
      console.error('Error removing job from folder:', error);
      setError('Failed to remove job from folder');
    }
  };

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleCreateRootFolder = () => {
    setNewFolderName('');
    setNewFolderDescription('');
    setNewFolderDialogOpen(true);
  };
  
  const handleSaveNewFolder = async () => {
    try {
      const newFolder = await createFolder({
        name: newFolderName,
        description: newFolderDescription || undefined,
        parent: null // Root folder
      });
      setNewFolderDialogOpen(false);
      
      // Increment the trigger to refresh the folder tree
      setFolderRefreshTrigger(prev => prev + 1);
      
      // Refresh the folders without page reload
      fetchJobs();
      
      // Select the newly created folder
      if (newFolder && newFolder.id) {
        setSelectedFolderId(newFolder.id);
        navigate('/jobs', { 
          state: { 
            selectedFolderId: newFolder.id,
            folderName: newFolder.name 
          },
          replace: true
        });
      }
    } catch (error) {
      console.error('Error creating folder:', error);
      setError('Failed to create folder');
    }
  };

  // Function to handle optimize button click
  const handleOptimizeClick = (event: React.MouseEvent) => {
    // For testing purposes, you can enable test mode by holding Shift while clicking
    const isTestMode = event.shiftKey;
    navigate('/optimize/new', { state: { testMode: isTestMode } });
  };

  if (loading && jobs.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box mb={4}>
        <Typography variant="h4" component="h1" gutterBottom>
          All Jobs
        </Typography>
        
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box display="flex">
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleNewJobClick}
              disabled={!hasCompleteAwsSettings || awsSettingsLoading}
              sx={{ mr: 2 }}
            >
              New Job
            </Button>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<TuneIcon />}
              onClick={handleOptimizeClick}
            >
              Optimize
            </Button>
          </Box>
          <Box display="flex">
            <Button
              startIcon={<RefreshIcon />}
              onClick={fetchJobs}
              sx={{ mr: 1 }}
            >
              Refresh
            </Button>
            <TextField
              size="small"
              placeholder="Search jobs..."
              value={searchTerm}
              onChange={handleSearch}
              InputProps={{
                startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
          </Box>
        </Box>
        
        <Grid container spacing={0}>
          <Grid item xs={12} md={3} lg={2.5} sx={{ display: { xs: drawerOpen ? 'block' : 'none', md: 'block' }, pr: 2 }}>
            <Paper sx={{ p: 2, height: '100%' }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  Folders
                </Typography>
                <IconButton
                  size="small"
                  onClick={handleCreateRootFolder}
                  sx={{ color: 'primary.main' }}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </Box>
              <FolderTree
                onFolderSelect={handleFolderSelect}
                selectedFolderId={selectedFolderId}
                refreshTrigger={folderRefreshTrigger}
              />
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={drawerOpen ? 9 : 12} lg={drawerOpen ? 9.5 : 12}>
            <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
              <Box display="flex" alignItems="center">
                <IconButton
                  size="small"
                  onClick={toggleDrawer}
                  sx={{ display: { xs: 'inline-flex', md: 'none' }, mr: 1 }}
                >
                  <FolderIcon fontSize="small" />
                </IconButton>
                <Typography variant="h6">
                  {selectedFolderId ? 'Folder Jobs' : 'All Jobs'}
                </Typography>
                {selectedFolderId && (
                  <Chip
                    label={`Filtered by Folder: ${location.state?.folderName || 'Unnamed Folder'}`}
                    color="primary"
                    variant="outlined"
                    sx={{ ml: 1 }}
                    onDelete={() => {
                      setSelectedFolderId(null);
                      navigate('/jobs', { state: {} });
                    }}
                  />
                )}
              </Box>
            
              <Box display="flex" alignItems="center">
                <FormControl size="small" sx={{ minWidth: 150, mr: 2 }}>
                  <InputLabel id="model-type-filter-label">Model Type</InputLabel>
                  <Select
                    labelId="model-type-filter-label"
                    value={filterModelType}
                    onChange={(e) => {
                      setFilterModelType(e.target.value);
                      setPage(1);
                    }}
                    label="Model Type"
                    sx={{ width: '100%' }}
                  >
                    <MenuItem value="">All Types</MenuItem>
                    <MenuItem value="colabfold">ColabFold</MenuItem>
                    <MenuItem value="rfdiffusion">RF Diffusion</MenuItem>
                    <MenuItem value="esmif">ESM-IF</MenuItem>
                  </Select>
                </FormControl>
                
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel id="status-filter-label">Status</InputLabel>
                  <Select
                    labelId="status-filter-label"
                    value={filterStatus}
                    onChange={(e) => {
                      setFilterStatus(e.target.value);
                      setPage(1);
                    }}
                    label="Status"
                    sx={{ width: '100%' }}
                  >
                    <MenuItem value="">All Statuses</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="running">Running</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                    <MenuItem value="failed">Failed</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>
            
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Job Name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Last Updated</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {jobs.map((job) => (
                    <React.Fragment key={job.id}>
                      <TableRow 
                        hover
                        onClick={() => handleViewJob(job)}
                        sx={{ 
                          cursor: 'pointer',
                          backgroundColor: job.is_batch ? 'rgba(0, 0, 0, 0.04)' : 'inherit'
                        }}
                      >
                        <TableCell>
                          {job.jobname}
                          {job.is_batch && (
                            <Chip 
                              size="small" 
                              label={`Batch (${job.child_jobs_count})`} 
                              color="primary" 
                              sx={{ ml: 1 }}
                            />
                          )}
                        </TableCell>
                        <TableCell>{getModelTypeLabel(job.model_type)}</TableCell>
                        <TableCell>
                          <Chip 
                            label={job.status} 
                            color={getStatusColor(job.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{new Date(job.created_at).toLocaleString()}</TableCell>
                        <TableCell>{new Date(job.updated_at).toLocaleString()}</TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center">
                            <IconButton 
                              onClick={(e) => {
                                e.stopPropagation();
                                if (job.is_batch) {
                                  toggleBatchExpand(job.id);
                                } else {
                                  handleJobActionsOpen(e, job);
                                }
                              }}
                            >
                              {job.is_batch ? (
                                expandedBatchJobs[job.id] ? <ExpandLess /> : <ExpandMore />
                              ) : (
                                <MoreIcon />
                              )}
                            </IconButton>
                            <IconButton
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteJobId(job.id);
                                setDeleteJobModelType(job.model_type);
                              }}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                      
                      {/* Child jobs row (batch jobs) */}
                      {job.is_batch && (
                        <TableRow>
                          <TableCell colSpan={6} style={{ paddingBottom: 0, paddingTop: 0 }}>
                            <Collapse in={expandedBatchJobs[job.id]} timeout="auto" unmountOnExit>
                              <Box sx={{ margin: 2 }}>
                                <Typography variant="h6" gutterBottom component="div">
                                  Batch Jobs
                                </Typography>
                                {loadingChildJobs[job.id] ? (
                                  <Box display="flex" justifyContent="center" p={2}>
                                    <CircularProgress size={24} />
                                  </Box>
                                ) : (
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell>Name</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Created</TableCell>
                                        <TableCell>Updated</TableCell>
                                        <TableCell>Actions</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {childJobs[job.id]?.map((childJob) => (
                                        <TableRow 
                                          key={childJob.id} 
                                          hover
                                          onClick={(e) => handleViewChildJob(childJob, e)}
                                          sx={{ cursor: 'pointer' }}
                                        >
                                          <TableCell>{childJob.jobname}</TableCell>
                                          <TableCell>
                                            <Chip 
                                              label={childJob.status} 
                                              color={getStatusColor(childJob.status)}
                                              size="small"
                                            />
                                          </TableCell>
                                          <TableCell>{new Date(childJob.created_at).toLocaleString()}</TableCell>
                                          <TableCell>{new Date(childJob.updated_at).toLocaleString()}</TableCell>
                                          <TableCell>
                                            <IconButton
                                              size="small"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setDeleteJobId(childJob.id);
                                                setDeleteJobModelType(childJob.model_type);
                                              }}
                                              color="error"
                                            >
                                              <DeleteIcon fontSize="small" />
                                            </IconButton>
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                      {(!childJobs[job.id] || childJobs[job.id].length === 0) && !loadingChildJobs[job.id] && (
                                        <TableRow>
                                          <TableCell colSpan={5} align="center">
                                            No child jobs found
                                          </TableCell>
                                        </TableRow>
                                      )}
                                    </TableBody>
                                  </Table>
                                )}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                  {jobs.length === 0 && !loading && (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        No jobs found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            
            {totalPages > 1 && (
              <Box display="flex" justifyContent="center" mt={3}>
                <Pagination 
                  count={totalPages} 
                  page={page} 
                  onChange={handlePageChange} 
                  color="primary" 
                />
              </Box>
            )}
          </Grid>
        </Grid>
      </Box>
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={Boolean(deleteJobId)}
        onClose={() => setDeleteJobId(null)}
      >
        <DialogTitle>Delete Job</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this job? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteJobId(null)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
      
      {/* Model Selection Dialog */}
      <Dialog
        open={modelSelectionOpen}
        onClose={() => setModelSelectionOpen(false)}
      >
        <DialogTitle>Select Model Type</DialogTitle>
        <List>
          <ListItem button onClick={() => handleModelSelect(ModelType.COLABFOLD)}>
            <ListItemIcon>
              <ScienceIcon />
            </ListItemIcon>
            <ListItemText primary="ColabFold" secondary="Protein structure prediction" />
          </ListItem>
          <ListItem button onClick={() => handleModelSelect(ModelType.RFDIFFUSION)}>
            <ListItemIcon>
              <ArchitectureIcon />
            </ListItemIcon>
            <ListItemText primary="RF Diffusion" secondary="Protein design and engineering" />
          </ListItem>
          <ListItem button onClick={() => handleModelSelect(ModelType.ESMIF)}>
            <ListItemIcon>
              <BiotechIcon />
            </ListItemIcon>
            <ListItemText primary="ESM-IF" secondary="Protein sequence design" />
          </ListItem>
        </List>
      </Dialog>
      
      {/* Folder Dialog */}
      <Dialog open={folderDialogOpen} onClose={() => setFolderDialogOpen(false)}>
        <DialogTitle>Move to Folder</DialogTitle>
        <DialogContent>
          <FolderTree
            onFolderSelect={handleAddJobToFolder}
            selectedFolderId={selectedJobForAction?.folder || null}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFolderDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={() => {
              if (selectedJobForAction) {
                handleAddJobToFolder(selectedJobForAction.folder);
              }
              setFolderDialogOpen(false);
            }}
            variant="contained"
            disabled={!selectedJobForAction}
          >
            Move
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* New Root Folder Dialog */}
      <Dialog open={newFolderDialogOpen} onClose={() => setNewFolderDialogOpen(false)}>
        <DialogTitle>Create New Folder</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Folder Name"
            fullWidth
            variant="outlined"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            required
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Description (Optional)"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={newFolderDescription}
            onChange={(e) => setNewFolderDescription(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewFolderDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleSaveNewFolder} 
            variant="contained"
            disabled={!newFolderName.trim()}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Job Actions Menu */}
      <Menu
        anchorEl={jobActionsAnchorEl}
        open={Boolean(jobActionsAnchorEl)}
        onClose={handleJobActionsClose}
      >
        <MenuItem onClick={() => selectedJobForAction && handleViewJob(selectedJobForAction)}>
          <ScienceIcon fontSize="small" sx={{ mr: 1 }} />
          View Details
        </MenuItem>
        <MenuItem onClick={handleAddToFolder}>
          <FolderIcon fontSize="small" sx={{ mr: 1 }} />
          Move to Folder
        </MenuItem>
        {selectedJobForAction?.folder && (
          <MenuItem onClick={handleRemoveFromFolder}>
            <FolderIcon fontSize="small" sx={{ mr: 1 }} />
            Remove from Folder
          </MenuItem>
        )}
        <MenuItem onClick={() => {
          if (selectedJobForAction) {
            setDeleteJobId(selectedJobForAction.id);
            setDeleteJobModelType(selectedJobForAction.model_type);
            handleJobActionsClose();
          }
        }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>
      
      {/* Quota Error Dialog */}
      {quotaError && (
        <Dialog open={Boolean(quotaError)} onClose={handleCloseQuotaError}>
          <DialogTitle>Error</DialogTitle>
          <DialogContent>{quotaError}</DialogContent>
          <DialogActions>
            <Button onClick={handleCloseQuotaError} color="primary">OK</Button>
          </DialogActions>
        </Dialog>
      )}
    </Container>
  );
};

export default AllJobsList; 